import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FileSpreadsheet, FileText, Download, Loader2, ClipboardList, Package, Users, CalendarIcon, X, Database, Upload, ShoppingCart, Droplets } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from '@/components/ui/label';

const DatePicker = ({ label, value, onChange, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={testId}
          className="w-full justify-start text-left font-normal h-9 text-sm"
        >
          <CalendarIcon className="mr-2 h-3.5 w-3.5 text-slate-400" />
          {value ? format(value, 'dd.MM.yyyy', { locale: nb }) : <span className="text-slate-400">Velg dato</span>}
          {value && (
            <X
              className="ml-auto h-3.5 w-3.5 text-slate-400 hover:text-slate-600"
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} locale={nb} initialFocus />
      </PopoverContent>
    </Popover>
  </div>
);

const DateRangeFilter = ({ from, to, onFromChange, onToChange, prefix }) => (
  <div className="flex flex-col sm:flex-row gap-3">
    <DatePicker label="Fra dato" value={from} onChange={onFromChange} testId={`${prefix}-from-date`} />
    <DatePicker label="Til dato" value={to} onChange={onToChange} testId={`${prefix}-to-date`} />
  </div>
);

const buildDateParams = (from, to) => {
  const params = new URLSearchParams();
  if (from) params.set('start_date', format(from, 'yyyy-MM-dd'));
  if (to) params.set('end_date', format(to, 'yyyy-MM-dd'));
  return params.toString();
};

const ExportButton = ({ onAction, loading, prefix, hasExcel = true }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid={`export-${prefix}-btn`} disabled={loading !== null} className="bg-slate-900 hover:bg-slate-800">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Rapport
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="space-y-0.5">
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 text-left" onClick={() => { setOpen(false); onAction('pdf', 'open'); }} data-testid={`${prefix}-open-pdf`}>
            <FileText className="h-4 w-4 text-red-500" /> Åpne PDF
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 text-left" onClick={() => { setOpen(false); onAction('pdf', 'download'); }} data-testid={`${prefix}-dl-pdf`}>
            <Download className="h-4 w-4 text-red-500" /> Last ned PDF
          </button>
          {hasExcel && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 text-left" onClick={() => { setOpen(false); onAction('excel', 'open'); }} data-testid={`${prefix}-open-excel`}>
                <FileSpreadsheet className="h-4 w-4 text-brand-500" /> Åpne Excel
              </button>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 text-left" onClick={() => { setOpen(false); onAction('excel', 'download'); }} data-testid={`${prefix}-dl-excel`}>
                <Download className="h-4 w-4 text-brand-500" /> Last ned Excel
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const Reports = () => {
  const { user } = useContext(AuthContext);
  const [loadingService, setLoadingService] = useState(null);
  const [loadingLoan, setLoadingLoan] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(null);
  const [loanStatus, setLoanStatus] = useState('all');
  const [serviceFrom, setServiceFrom] = useState(null);
  const [serviceTo, setServiceTo] = useState(null);
  const [loanFrom, setLoanFrom] = useState(null);
  const [loanTo, setLoanTo] = useState(null);
  const [orderFrom, setOrderFrom] = useState(null);
  const [orderTo, setOrderTo] = useState(null);
  const [orderStatus, setOrderStatus] = useState('all');
  const [orderCustomer, setOrderCustomer] = useState('all');
  const [orderCategory, setOrderCategory] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCleaning, setLoadingCleaning] = useState(false);
  const [cleaningFilter, setCleaningFilter] = useState('alle');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [custRes, catRes] = await Promise.all([
          axios.get(`${API}/customers`),
          axios.get(`${API}/product-categories`),
        ]);
        setCustomers(custRes.data.filter(c => c.is_active !== false));
        setCategories(catRes.data);
      } catch (err) { console.warn('[Reports]', err); }
    };
    fetchFilters();
  }, []);

  const downloadFile = async (url, filename) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Rapport lastet ned!');
    } catch (error) {
      toast.error('Kunne ikke laste ned rapport');
    }
  };

  const openFile = async (url) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('Kunne ikke åpne rapport');
    }
  };

  const handleServiceExport = async (fmt, action = 'download') => {
    setLoadingService(fmt);
    try {
      const dateParams = buildDateParams(serviceFrom, serviceTo);
      const sep = dateParams ? '?' + dateParams : '';
      const url = `${API}/reports/services/${fmt}${sep}`;
      if (action === 'open') await openFile(url);
      else await downloadFile(url, `service_rapport_${new Date().toISOString().slice(0,10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
    } finally { setLoadingService(null); }
  };

  const handleLoanExport = async (fmt, action = 'download') => {
    setLoadingLoan(fmt);
    try {
      const params = new URLSearchParams();
      if (loanStatus !== 'all') params.set('status', loanStatus);
      if (loanFrom) params.set('start_date', format(loanFrom, 'yyyy-MM-dd'));
      if (loanTo) params.set('end_date', format(loanTo, 'yyyy-MM-dd'));
      const sep = params.toString() ? '?' + params.toString() : '';
      const url = `${API}/reports/loans/${fmt}${sep}`;
      if (action === 'open') await openFile(url);
      else await downloadFile(url, `utlaans_rapport_${new Date().toISOString().slice(0,10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
    } finally { setLoadingLoan(null); }
  };

  const handleCustomerExport = async (fmt, action = 'download') => {
    setLoadingCustomer(fmt);
    try {
      const url = `${API}/reports/customers/${fmt}`;
      if (action === 'open') await openFile(url);
      else await downloadFile(url, `kunde_rapport_${new Date().toISOString().slice(0,10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
    } finally { setLoadingCustomer(null); }
  };

  const handleOrderExport = async (fmt, action = 'download') => {
    setLoadingOrder(fmt);
    try {
      const params = new URLSearchParams();
      if (orderFrom) params.set('start_date', format(orderFrom, 'yyyy-MM-dd'));
      if (orderTo) params.set('end_date', format(orderTo, 'yyyy-MM-dd'));
      if (orderStatus !== 'all') params.set('status', orderStatus);
      if (orderCustomer !== 'all') params.set('customer_id', orderCustomer);
      if (orderCategory !== 'all') params.set('category_id', orderCategory);
      const sep = params.toString() ? '?' + params.toString() : '';
      const url = `${API}/reports/orders/${fmt}${sep}`;
      if (action === 'open') await openFile(url);
      else await downloadFile(url, `bestillingsrapport_${new Date().toISOString().slice(0,10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
    } finally { setLoadingOrder(null); }
  };

  const handleCleaningExport = async (fmt = 'pdf', action = 'download') => {
    setLoadingCleaning(true);
    try {
      const params = new URLSearchParams();
      params.set('filter_type', cleaningFilter);
      const url = `${API}/reports/cleaning/pdf?${params.toString()}`;
      if (action === 'open') await openFile(url);
      else await downloadFile(url, `renseliste_${cleaningFilter}_${new Date().toISOString().slice(0,10)}.pdf`);
    } finally { setLoadingCleaning(false); }
  };

  const handleDataExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/migration/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tapservice_backup_${new Date().toISOString().slice(0,10)}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data eksportert!');
    } catch (error) {
      toast.error('Kunne ikke eksportere data');
    } finally {
      setExporting(false);
    }
  };

  const handleDataImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/migration/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const r = res.data.results;
      const total = Object.values(r).reduce((sum, v) => sum + v.imported, 0);
      toast.success(`Import fullført! ${total} poster importert.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke importere data');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Rapporter
        </h1>
        <p className="text-slate-600">Last ned service-, utlåns- og kunderapporter</p>
      </div>

      <div className="grid gap-6">
        {/* Service Rapport */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-100 rounded-lg">
                <ClipboardList className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <CardTitle>Service Rapport</CardTitle>
                <CardDescription>Eksporter alle servicer med kunde, type, tekniker og notater</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <DateRangeFilter
              from={serviceFrom} to={serviceTo}
              onFromChange={setServiceFrom} onToChange={setServiceTo}
              prefix="service"
            />
            <ExportButton onAction={handleServiceExport} loading={loadingService} prefix="service" />
          </CardContent>
        </Card>

        {/* Utlåns Rapport */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <CardTitle>Utlåns Rapport</CardTitle>
                <CardDescription>Eksporter utlånt utstyr med kunde, serienummer og status</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="space-y-1 flex-shrink-0">
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={loanStatus} onValueChange={setLoanStatus}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="loan-status-filter">
                    <SelectValue placeholder="Velg status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="active">Kun aktive</SelectItem>
                    <SelectItem value="returned">Kun returnerte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DateRangeFilter
                from={loanFrom} to={loanTo}
                onFromChange={setLoanFrom} onToChange={setLoanTo}
                prefix="loan"
              />
            </div>
            <ExportButton onAction={handleLoanExport} loading={loadingLoan} prefix="loan" />
          </CardContent>
        </Card>

        {/* Kunderapport */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Users className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <CardTitle>Kunderapport</CardTitle>
                <CardDescription>Eksporter kundeliste med produkter og utstyr per kunde</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ExportButton onAction={handleCustomerExport} loading={loadingCustomer} prefix="customer" />
          </CardContent>
        </Card>

        {/* Renseliste PDF */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Droplets className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <CardTitle>Renseliste</CardTitle>
                <CardDescription>Utskriftsvennlig A4-rapport for planlegging av renseoppdrag</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Filter</Label>
              <Select value={cleaningFilter} onValueChange={setCleaningFilter}>
                <SelectTrigger className="w-full sm:w-[260px] h-9" data-testid="cleaning-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle aktive kunder</SelectItem>
                  <SelectItem value="kritisk">Kritiske (forfalt + denne uken)</SelectItem>
                  <SelectItem value="vanlig">Vanlige (kommende + planlagt)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ExportButton onAction={handleCleaningExport} loading={loadingCleaning ? 'pdf' : null} prefix="cleaning" hasExcel={false} />
          </CardContent>
        </Card>

        {/* Datamigrering - kun for admin */}
        {(user?.role === 'admin' || user?.role === 'system_admin') && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Database className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                  <CardTitle>Datamigrering</CardTitle>
                  <CardDescription>Eksporter og importer data mellom miljøer</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="export-data-button"
                  onClick={handleDataExport}
                  disabled={exporting}
                  className="flex-1 bg-brand-600 hover:bg-brand-700"
                >
                  {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  {exporting ? 'Eksporterer...' : 'Eksporter all data'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleDataImport}
                  className="hidden"
                />
                <Button
                  data-testid="import-data-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  variant="outline"
                  className="flex-1"
                >
                  {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {importing ? 'Importerer...' : 'Importer data'}
                </Button>
              </div>
              <p className="text-xs text-slate-500">Eksport laster ned all data som JSON. Import oppdaterer eksisterende poster og legger til nye.</p>
            </CardContent>
          </Card>
        )}

        {/* Info */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Download className="h-5 w-5 text-slate-500 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Om rapportene</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Excel-filer kan åpnes i Microsoft Excel, Google Sheets eller Numbers</li>
                  <li>PDF-filer er klare for utskrift eller arkivering</li>
                  <li>Rapportene inneholder kun data for din bedrift</li>
                  <li>Bruk fra/til-dato for å filtrere på ønsket periode</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
